import { ReactElement, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { AxiosResultDefaultError } from '../../../api/request';
import { createFetchContext } from '../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../components/info/DisplayError';
import Loading from '../../../components/info/Loading';
import { getSurveyData } from '../api';
import { SurveyResponse } from '../types';

export interface TeacherSurveyLoaderProps {
  children: (props: {
    surveyData: SurveyResponse;
    refresh: () => Promise<void>;
  }) => ReactElement;
}

const { FetchProvider, FetchConsumer, useFetch } = createFetchContext<
  undefined,
  SurveyResponse,
  AxiosResultDefaultError
>();

export function useTeacherSurveyLoader() {
  return useFetch();
}

export default function TeacherSurveyLoader({
  children,
}: TeacherSurveyLoaderProps) {
  const { id, teacherId } = useParams<{
    id: string;
    teacherId: string;
  }>();

  const request = useCallback(async () => {
    const req = await getSurveyData(id, teacherId);
    return req;
  }, [id, teacherId]);

  return (
    <FetchProvider request={request} defaultQuery={undefined} fetchImmediately>
      <FetchConsumer>
        {({ loading, data, error, refresh }) => {
          if (loading) {
            return <Loading insideCard />;
          }

          if (error) {
            return (
              <DisplayError
                insideCard
                body={error.code}
                retryAction={refresh}
              />
            );
          }

          if (!data) {
            return (
              <DisplayError
                insideCard
                body="No se cargaron los datos"
                retryAction={refresh}
              />
            );
          }

          return children({ surveyData: data, refresh });
        }}
      </FetchConsumer>
    </FetchProvider>
  );
}
